import { copy } from './copy';
import type { ReportId } from './types';

/**
 * The four sections, described once.
 *
 * Everything here is a **rendering** decision the wire deliberately leaves on
 * this side of the hop — which figure is named what, which column header a
 * section carries, which link leaves it. None of it is a vocabulary: the four
 * section ids are the service's own `StrEnum`, and the tile keys are code-pinned
 * in the same module that composes them.
 *
 * `denominatorTileKey` is the load-bearing field. A share bar is a proportion,
 * and a proportion needs a whole — but the payload carries no share and no
 * percentage, so the console computes both. Naming the tile that holds the whole
 * is what keeps that computation honest on the one section where summing the
 * rows would be wrong: the content breakdown carries **two partitions in one
 * array** — four publish states and two content types — and both sum to the
 * total, so a denominator of «the sum of the rows» would halve every share on
 * that table. Where no tile holds the whole, the rows are the whole and the sum
 * is correct.
 */
export interface ReportSectionSpec {
  id: ReportId;
  heading: string;
  /** The surface that acts on what this section reports. */
  linkTo: string;
  linkLabel: string;
  caption: string;
  rowColumn: string;
  valueColumn: string;
  /** Three figures take three columns; a fourth empty cell reads as a missing figure. */
  statColumns: 3 | 4;
  /** The tile holding the whole this section's rows are a share of. */
  denominatorTileKey: string | null;
  /** The tile the `<tfoot>` total restates, where the section carries one. */
  totalTileKey: string | null;
  /** Figures that are a queue awaiting operator work — the one meaning the accent may carry. */
  pendingTileKeys: readonly string[];
  /**
   * Breakdown rows that are **queue depths rather than kinds of the thing the
   * table partitions**, drawn in their own block and excluded from the share.
   *
   * The interaction table is «التفاعل المنشور موزّعاً بحسب نوعه» and its
   * denominator is the sum of its rows, which only works while the rows are
   * disjoint kinds of one whole. Three of them are not: comments awaiting
   * review are a subset of the comments row, and open reports and open cases
   * are not interactions at all. The arithmetic was visibly wrong on develop —
   * ١٥٩ comments and ٢٤٩ held read as ٥٩٪ and ٤٠٪ of a total that counts the
   * held ones twice — and it gets worse, not better, the more work is queued.
   */
  queueDepthRowKeys: readonly string[];
  /** Subscription surfaces take the editorial indigo the design system assigns them by name. */
  shareTone: 'default' | 'subscription';
  /** Money rows carry no unit until the currency has a route this session can read. */
  valuesAreMoney: boolean;
}

export const REPORT_SECTIONS: readonly ReportSectionSpec[] = [
  {
    id: 'users',
    heading: copy.sections.users.heading,
    linkTo: '/users',
    linkLabel: copy.sections.users.link,
    caption: copy.captions.users,
    rowColumn: copy.columns.users.row,
    valueColumn: copy.columns.users.value,
    statColumns: 4,
    denominatorTileKey: 'account_count',
    totalTileKey: null,
    pendingTileKeys: [],
    queueDepthRowKeys: [],
    shareTone: 'default',
    valuesAreMoney: false,
  },
  {
    id: 'content',
    heading: copy.sections.content.heading,
    linkTo: '/content',
    linkLabel: copy.sections.content.link,
    caption: copy.captions.content,
    rowColumn: copy.columns.content.row,
    valueColumn: copy.columns.content.value,
    statColumns: 4,
    denominatorTileKey: 'total_titles',
    totalTileKey: null,
    pendingTileKeys: [],
    queueDepthRowKeys: [],
    shareTone: 'default',
    valuesAreMoney: false,
  },
  {
    id: 'engagement',
    heading: copy.sections.engagement.heading,
    linkTo: '/comments',
    linkLabel: copy.sections.engagement.link,
    caption: copy.captions.engagement,
    rowColumn: copy.columns.engagement.row,
    valueColumn: copy.columns.engagement.value,
    statColumns: 4,
    denominatorTileKey: null,
    totalTileKey: null,
    // **Every key that is a queue awaiting operator work**, which is the one
    // meaning the accent carries. Naming only the keys that happen to be tiles
    // in today's payload is a rule that goes wrong the next time the owning
    // service moves one — and it did: all three arrive as tiles now.
    pendingTileKeys: ['comment_moderation_queue_depth', 'report_queue_depth', 'open_case_count'],
    queueDepthRowKeys: ['comment_moderation_queue_depth', 'report_queue_depth', 'open_case_count'],
    shareTone: 'default',
    valuesAreMoney: false,
  },
  {
    id: 'revenue',
    heading: copy.sections.revenue.heading,
    linkTo: '/subscriptions',
    linkLabel: copy.sections.revenue.link,
    caption: copy.captions.revenue,
    rowColumn: copy.columns.revenue.row,
    valueColumn: copy.columns.revenue.value,
    statColumns: 4,
    denominatorTileKey: 'total_amount',
    totalTileKey: 'total_amount',
    pendingTileKeys: [],
    queueDepthRowKeys: [],
    shareTone: 'subscription',
    valuesAreMoney: true,
  },
];
